<template>
	<v-container
		fluid
		:class = "['access-control-device-page', embedded ? 'access-control-device-page--embedded pa-0' : 'pa-4']"
	>
		<v-row no-gutters>
			<v-col cols = "12">
				<v-card outlined class = "access-control-device-card access-control-settings-region access-control-settings-region--device">
					<v-card-title class = "justify-center body-1 secondary pa-1 white--text" v-if = "!embedded">
						<v-icon small color = "white" class = "ml-1" @click = "$router.back()">mdi-arrow-left-circle</v-icon>
						<v-spacer></v-spacer>
						<v-icon color = "primary" class = "mr-2">mdi-door-closed-lock</v-icon>
						<span>ACCESS CONTROL SETTINGS</span>
						<v-spacer></v-spacer>
					</v-card-title>

					<v-card-title class = "pa-0 caption secondary white--text vue-draggable-handle" v-else>
						<v-icon x-small color = "primary" class = "px-2">mdi-video</v-icon>
						<span>{{ $ml.get('device') }}</span>
					</v-card-title>

					<v-card-text class = "pb-1" v-if = "!embedded">
						<v-form ref = "form" v-model = "valid" class = "access-control-settings-form">
							<v-chip-group column class = "mb-3">
								<v-chip
									v-for = "(step, index) of accessWizardSteps"
									:key = "step.key"
									small
									:color = "index == wizardStepIndex ? 'primary' : ''"
									:outlined = "index != wizardStepIndex"
									@click = "goToWizardStep(index)"
								>
									{{ step.number }}. {{ $ml.get(step.title) }}
								</v-chip>
							</v-chip-group>

							<v-card outlined class = "access-control-wizard-step mb-3" v-if = "currentWizardStep">
								<v-card-title class = "py-2 d-flex align-start flex-nowrap">
									<v-avatar size = "28" color = "primary" class = "mr-4 flex-shrink-0">
										<span class = "caption white--text font-weight-bold">{{ currentWizardStep.number }}</span>
									</v-avatar>
									<div>
										<div class = "subtitle-2 font-weight-bold">{{ $ml.get(currentWizardStep.title) }}</div>
										<div class = "caption grey--text text--lighten-1 mt-1">{{ $ml.get(currentWizardStep.help) }}</div>
									</div>
								</v-card-title>
								<v-card-text class = "pt-0">
									<template v-if = "currentWizardStep.key == 'vendor'">
										<v-select
											dense
											outlined
											v-model = "form.vendor"
											:label = "$ml.get('access_control_vendor')"
											:items = "vendorOptions"
											item-text = "label"
											item-value = "value"
											:loading = "vendorLoading"
											:rules = "requiredRules"
											@change = "onVendorChange"
										></v-select>
										<v-alert v-if = "vendorLoadMessage" dense outlined type = "info" class = "mb-0">
											{{ vendorLoadMessage }}
										</v-alert>
									</template>

									<template v-else-if = "currentWizardStep.key == 'model'">
										<v-select
											v-if = "modelOptions.length"
											dense
											outlined
											v-model = "form.model"
											:label = "$ml.get('access_control_integrated_model')"
											:items = "modelOptions"
											item-text = "label"
											item-value = "value"
											:loading = "modelLoading"
											:rules = "requiredRules"
											@change = "scheduleTerminalProbe"
										></v-select>
										<v-text-field
											v-else
											dense
											outlined
											v-model = "form.model"
											:label = "$ml.get('access_control_integrated_model')"
											:hint = "$ml.get('access_control_model_manual_hint')"
											persistent-hint
											@input = "scheduleTerminalProbe"
										></v-text-field>
										<v-alert v-if = "modelLoadMessage" dense outlined type = "info" class = "mb-0">
											{{ modelLoadMessage }}
										</v-alert>
									</template>

									<template v-else-if = "currentWizardStep.key == 'terminal'">
										<v-row dense>
											<v-col cols = "12" md = "6">
												<v-text-field dense outlined v-model = "form.name" :label = "$ml.get('name')" :rules = "requiredRules"></v-text-field>
											</v-col>
											<v-col cols = "12" md = "6">
												<v-text-field dense outlined v-model = "form.host" :label = "$ml.get('ip') || 'IP'" :rules = "requiredRules" @input = "scheduleTerminalProbe"></v-text-field>
											</v-col>
											<v-col cols = "12" md = "6">
												<v-text-field dense outlined v-model = "form.username" :label = "$ml.get('username') || 'Usuario'" :rules = "requiredRules" @input = "scheduleTerminalProbe"></v-text-field>
											</v-col>
											<v-col cols = "12" md = "6">
												<v-text-field
													dense
													outlined
													:type = "passwordFieldType"
													v-model = "form.password"
													:label = "$ml.get('password') || 'Clave'"
													:placeholder = "passwordPlaceholder"
													:hint = "passwordHint"
													:persistent-hint = "isEdit"
													:rules = "passwordRules"
													:append-icon = "passwordAppendIcon"
													:loading = "passwordRevealLoading"
													@click:append = "togglePasswordVisibility"
													@input = "scheduleTerminalProbe"
												></v-text-field>
											</v-col>
											<v-col cols = "12" md = "6">
												<v-text-field dense outlined v-model = "form.serial" :label = "$ml.get('serial') || 'Serial'"></v-text-field>
											</v-col>
										</v-row>
										<v-alert v-if = "probeMessage" dense outlined :type = "probeMessageType" class = "mb-2">
											{{ probeMessage }}
										</v-alert>
										<v-btn small outlined color = "primary" :loading = "probeLoading" :disabled = "!canProbeTerminal" @click = "probeTerminalInfo(false)">
											<v-icon left small>mdi-radar</v-icon>
											{{ $ml.get('access_control_probe_terminal') }}
										</v-btn>
									</template>

									<template v-else-if = "currentWizardStep.key == 'video'">
										<v-text-field
											dense
											outlined
											v-model = "form.rtsp"
											:label = "$ml.get('access_control_rtsp_url')"
											placeholder = "rtsp://host:554/cam/realmonitor"
											:rules = "rtspRules"
											:append-icon = "showPreview ? 'mdi-eye-off' : 'mdi-cctv'"
											@click:append = "testStream"
										></v-text-field>
										<v-alert dense outlined type = "info" class = "mb-2">
											{{ $ml.get('access_control_rtsp_preview_help') }}
										</v-alert>
										<v-alert v-if = "previewError" dense outlined type = "warning" class = "mb-2">
											{{ previewError }}
										</v-alert>
										<v-card v-if = "shouldRenderInternalPreview" outlined class = "access-control-preview-card">
											<v-card-title class = "caption py-1">
												<v-icon small color = "primary" class = "mr-1">mdi-cctv</v-icon>
												{{ $ml.get('access_control_rtsp_preview') }}
											</v-card-title>
											<v-card-text class = "pa-0 access-control-preview-frame">
												<cam
													:id_stream = "previewStreamId"
													:layout_id = "previewStreamId"
													:type = "'rtsp'"
													:hostname = "previewHostname"
													:name = "previewHostname"
													:info_stream = "previewInfo"
													:show_bar = "false"
													@preview_failed = "handlePreviewFailure"
													@preview-error = "handlePreviewFailure"
													@error.native = "handlePreviewFailure"
												></cam>
											</v-card-text>
										</v-card>
									</template>

									<template v-else-if = "currentWizardStep.key == 'location'">
										<v-alert v-if = "serverLocationMessage" dense outlined :type = "serverLocationMessageType" class = "mb-2">
											{{ serverLocationMessage }}
										</v-alert>
										<EditableMapComponent
											:title = "$ml.get('location') || 'Location'"
											:info_to_component = "mapLocation"
											@receiveInfo = "setLocation"
										></EditableMapComponent>
										<div v-if = "showLocationRequired" class = "error--text caption mt-1">
											{{ $ml.get('location') || 'Location' }} {{ $ml.get('required') || 'required' }}
										</div>
									</template>
								</v-card-text>
							</v-card>
						</v-form>
					</v-card-text>

					<v-card-text class = "pa-1 pb-0 access-control-device-card__embedded-body" v-else>
						<v-form ref = "form" v-model = "valid" class = "access-control-settings-form">
							<v-tabs
								dark
								fixed-tabs
								v-model = "tab"
								background-color = "primary"
								height = "20"
							>
								<v-tab>INFO</v-tab>
								<v-tab>MAP</v-tab>
							</v-tabs>

							<v-tabs-items v-model = "tab">
								<v-tab-item>
									<v-row dense class = "mt-1 access-control-settings-region access-control-settings-region--embedded-info access-control-settings-region--identity">
										<v-col cols = "12" class = "access-control-settings-region--channel access-control-settings-region--media access-control-settings-field--rtsp">
											<label class = "caption grey--text">Channel 1</label>
											<v-text-field dense outlined hide-details v-model = "form.rtsp" prepend-inner-icon = "mdi-video" label = "RTSP"></v-text-field>
											<div class = "caption access-control-stream-links">
												<div><strong class = "green--text">LOCAL</strong> {{ localRtsp }}</div>
												<div><strong class = "orange--text">HLS</strong> {{ hlsUrl }}</div>
											</div>
										</v-col>

										<v-col cols = "6" class = "access-control-settings-field--identity">
											<v-text-field dense outlined v-model = "form.name" :label = "$ml.get('name')" prepend-inner-icon = "mdi-map-marker" counter = "30"></v-text-field>
										</v-col>
										<v-col cols = "6" v-if = "isEdit && form.deviceId" class = "access-control-settings-field--identity">
											<v-text-field dense outlined :readonly = "isEdit" v-model = "form.deviceId" label = "Device ID" prepend-inner-icon = "mdi-video" :rules = "requiredRules"></v-text-field>
										</v-col>

										<v-col cols = "6" class = "access-control-settings-field--connection">
											<v-text-field dense outlined v-model = "form.host" :label = "$ml.get('ip') || 'Host/IP'" prepend-inner-icon = "mdi-ip" :rules = "requiredRules"></v-text-field>
										</v-col>
										<v-col cols = "6" class = "access-control-settings-field--connection">
											<v-text-field dense outlined v-model = "form.username" :label = "$ml.get('username') || 'Username'" prepend-inner-icon = "mdi-account" :rules = "requiredRules"></v-text-field>
										</v-col>
										<v-col cols = "6" class = "access-control-settings-field--connection">
											<v-text-field dense outlined :type = "passwordFieldType" v-model = "form.password" :label = "$ml.get('password') || 'Password'" :placeholder = "passwordPlaceholder" prepend-inner-icon = "mdi-lock" :hint = "passwordHint" :persistent-hint = "isEdit" :rules = "passwordRules" :append-icon = "passwordAppendIcon" :loading = "passwordRevealLoading" @click:append = "togglePasswordVisibility"></v-text-field>
										</v-col>
										<v-col cols = "6" v-if = "isEdit && form.serial">
											<v-text-field dense outlined readonly v-model = "form.serial" label = "Serial" prepend-inner-icon = "mdi-barcode"></v-text-field>
										</v-col>
										<v-col cols = "6" v-if = "isEdit && form.model">
											<v-text-field dense outlined readonly v-model = "form.model" label = "Model" prepend-inner-icon = "mdi-devices"></v-text-field>
										</v-col>
									</v-row>
								</v-tab-item>

								<v-tab-item>
									<div class = "access-control-location-selector access-control-settings-region access-control-settings-region--location access-control-location-selector--embedded">
										<EditableMapComponent
											:title = "$ml.get('location') || 'Location'"
											:info_to_component = "mapLocation"
											@receiveInfo = "setLocation"
										></EditableMapComponent>
									</div>
								</v-tab-item>
							</v-tabs-items>
						</v-form>
					</v-card-text>
					<v-divider></v-divider>
					<v-card-actions>
						<template v-if = "!embedded">
							<v-btn small outlined :disabled = "wizardStepIndex <= 0" @click = "previousWizardStep">
								<v-icon left small>mdi-chevron-left</v-icon>
								{{ $ml.get('previous') }}
							</v-btn>
							<v-spacer></v-spacer>
							<v-btn v-if = "!isLastWizardStep" small color = "primary" @click = "nextWizardStep">
								{{ $ml.get('next') }}
								<v-icon right small>mdi-chevron-right</v-icon>
							</v-btn>
							<v-btn v-else small color = "primary" :loading = "loading" @click = "save">
								{{ isEdit ? $ml.get('update') : $ml.get('register') }}
							</v-btn>
						</template>
						<template v-else>
							<v-spacer></v-spacer>
							<v-btn x-small color = "primary" :loading = "loading" @click = "save">
								{{ isEdit ? $ml.get('update') : $ml.get('register') }}
							</v-btn>
							<v-spacer></v-spacer>
						</template>
					</v-card-actions>
				</v-card>
			</v-col>
		</v-row>
	</v-container>
</template>

<script>
	import EventBus from '@/event-bus'
	import EditableMapComponent from '@/components/EditableMapComponent.vue'
	import cam from '@/components/cam.vue'
	import AccessControlService, { sanitizeAccessControlDevice, normalizeAccessControlVendor } from '@/services/accessControl.service.js'

	const emptyForm = () => ({
		id: null,
		deviceId: '',
		vendor: 'dahua',
		name: '',
		brand: '',
		model: '',
		host: '',
		username: '',
		password: '',
		serial: '',
		longitude: '',
		latitude: '',
		rtsp: '',
		type: 'ACCESS_CONTROL',
	})

	export default {
		name: 'access_control_device',
		components: {
			EditableMapComponent,
			cam,
		},
		props: [
			'info_to_device',
			'info_device',
			'embedded',
			'external_media_preview',
		],
		data: () => ({
			tab: null,
			valid: false,
			loading: false,
			form: emptyForm(),
			hasPassword: false,
			locationTouched: false,
			saveAttempted: false,
			showPreview: false,
			previewError: '',
			showPassword: false,
			passwordRevealLoading: false,
			wizardStepIndex: 0,
			modelOptions: [],
			vendorLoading: false,
			modelLoading: false,
			probeLoading: false,
			serverLocationLoading: false,
			vendorLoadMessage: '',
			modelLoadMessage: '',
			probeMessage: '',
			probeMessageType: 'info',
			serverLocationMessage: '',
			serverLocationMessageType: 'info',
			probeTimer: null,
			nameTouched: false,
			rtspTouched: false,
			vendorOptions: [
				{ label: 'Dahua', value: 'dahua' },
				{ label: 'Hikvision', value: 'hikvision' },
			],
			requiredRules: [value => !!value || 'Required'],
			rtspRules: [value => !value || /^rtsp:\/\//i.test(value) || 'RTSP URL must start with rtsp://'],
		}),
		computed: {
			accessWizardSteps(){
				return [
					{ key: 'vendor', number: 1, title: 'access_control_step_vendor_title', help: 'access_control_step_vendor_help' },
					{ key: 'model', number: 2, title: 'access_control_step_model_title', help: 'access_control_step_model_help' },
					{ key: 'terminal', number: 3, title: 'access_control_step_terminal_title', help: 'access_control_step_terminal_help' },
					{ key: 'video', number: 4, title: 'access_control_step_video_title', help: 'access_control_step_video_help' },
					{ key: 'location', number: 5, title: 'access_control_step_location_title', help: 'access_control_step_location_help' },
				]
			},
			currentWizardStep(){
				return this.accessWizardSteps[Math.min(Math.max(this.wizardStepIndex, 0), this.accessWizardSteps.length - 1)]
			},
			isLastWizardStep(){
				return this.wizardStepIndex >= this.accessWizardSteps.length - 1
			},
			canProbeTerminal(){
				return !!(this.form.vendor && this.form.host && this.form.username && this.form.password)
			},
			localRtsp(){
				return this.form.rtsp || '-'
			},
			hlsUrl(){
				return this.buildHlsUrl(this.form.deviceId, 1)
			},
			isEdit(){
				return !!(this.form.id || this.info_to_device || this.info_device)
			},
			passwordRules(){
				return this.isEdit ? [] : this.requiredRules
			},
			passwordHint(){
				if (!this.isEdit || !this.hasPassword || this.form.password) return ''
				return this.canEditDevice ? 'Saved credential available. Use the eye icon to reveal it or enter a new password to rotate it.' : 'Saved credential available. Enter a new password only when rotating it.'
			},
			passwordPlaceholder(){
				return this.isEdit && this.hasPassword && !this.form.password ? '••••••••' : ''
			},
			passwordFieldType(){
				return this.showPassword ? 'text' : 'password'
			},
			passwordAppendIcon(){
				if (this.isEdit && this.hasPassword && !this.form.password && !this.canEditDevice) return ''
				return this.showPassword ? 'mdi-eye-off' : 'mdi-eye'
			},
			canEditDevice(){
				const getter = this.$store && this.$store.getters ? this.$store.getters['auth/permission'] : null
				if (typeof getter !== 'function') return false
				return !!getter(this.$route.name || 'new_access_control', 'can_edit', 'device')
			},
			mapLocation(){
				return this.hasLocation ? {
					longitude: this.form.longitude,
					latitude: this.form.latitude,
				} : null
			},
			hasLocation(){
				return this.hasCoordinate(this.form.longitude) && this.hasCoordinate(this.form.latitude)
			},
			showLocationRequired(){
				return this.saveAttempted && !this.hasLocation
			},
			isRtspValid(){
				return !this.form.rtsp || /^rtsp:\/\//i.test(this.form.rtsp)
			},
			previewStreamId(){
				return this.form.deviceId || 'access-control-preview'
			},
			previewHostname(){
				return this.form.name || this.form.host || 'ACCESS CONTROL'
			},
			previewInfo(){
				return {
					...this.form,
					rtsp: this.form.rtsp,
					type: 'ACCESS_CONTROL',
				}
			},
			backendRtspDependencyMessage(){
				return this.$ml.get('access_control_rtsp_backend_dependency')
			},
			previewFailureMessage(){
				return this.$ml.get('access_control_rtsp_preview_unavailable')
			},
			backendDeviceIdContractMessage(){
				return this.$ml.get('access_control_backend_device_id_contract')
			},
			shouldRenderInternalPreview(){
				return this.showPreview && !this.external_media_preview
			},
		},
		watch: {
			info_to_device: {
				handler(val){
					if (val) this.renderInfo(val)
				},
				immediate: true,
			},
			info_device: {
				handler(val){
					if (val) this.renderInfo(val)
				},
				immediate: true,
			},
		},
		created(){
			if (!this.info_to_device && !this.info_device && this.shouldHydrateFromLocalSettings()) {
				this.loadFromLocalSettings()
			}
			this.loadVendors()
			this.loadVendorModels(this.form.vendor)
			if (!this.isEdit) this.loadServerLocation()
		},
		methods: {
			fallbackVendorOptions(){
				return [
					{ label: 'Dahua', value: 'dahua' },
					{ label: 'Hikvision', value: 'hikvision' },
				]
			},
			extractRows(payload){
				if (Array.isArray(payload)) return payload
				if (!payload || typeof payload !== 'object') return []
				if (Array.isArray(payload.rows)) return payload.rows
				if (Array.isArray(payload.results)) return payload.results
				if (Array.isArray(payload.data)) return payload.data
				if (Array.isArray(payload.items)) return payload.items
				if (Array.isArray(payload.vendors)) return payload.vendors
				if (Array.isArray(payload.models)) return payload.models
				if (payload.data && typeof payload.data === 'object') return this.extractRows(payload.data)
				return []
			},
			normalizeVendorRows(rows){
				const normalized = this.extractRows(rows).map(item => {
					const value = normalizeAccessControlVendor(item.vendor || item.value || item.key || item.name || item.label)
					if (!value) return null
					return { label: item.label || item.name || value, value }
				}).filter(Boolean)
				return normalized.length ? normalized : this.fallbackVendorOptions()
			},
			normalizeModelRows(rows){
				return this.extractRows(rows).map(item => {
					const value = item.model || item.value || item.key || item.name || item.label
					if (!value) return null
					return {
						label: item.label || item.name || value,
						value,
						raw: item,
					}
				}).filter(Boolean)
			},
			async loadVendors(){
				this.vendorLoading = true
				this.vendorLoadMessage = ''
				try {
					const res = await AccessControlService.listVendors()
					this.vendorOptions = this.normalizeVendorRows(res && res.data ? res.data : res)
				} catch (e) {
					this.vendorOptions = this.fallbackVendorOptions()
					this.vendorLoadMessage = this.$ml.get('access_control_vendor_fallback')
				} finally {
					this.vendorLoading = false
				}
			},
			async loadVendorModels(vendor){
				const normalizedVendor = normalizeAccessControlVendor(vendor)
				this.modelOptions = []
				this.modelLoadMessage = ''
				if (!normalizedVendor) return
				this.modelLoading = true
				try {
					const res = await AccessControlService.listVendorModels(normalizedVendor)
					this.modelOptions = this.normalizeModelRows(res && res.data ? res.data : res)
					if (!this.modelOptions.length) this.modelLoadMessage = this.$ml.get('access_control_model_manual_hint')
				} catch (e) {
					this.modelLoadMessage = this.$ml.get('access_control_models_unavailable')
				} finally {
					this.modelLoading = false
				}
			},
			onVendorChange(){
				this.form.vendor = normalizeAccessControlVendor(this.form.vendor) || this.form.vendor
				this.form.model = ''
				this.loadVendorModels(this.form.vendor)
				this.scheduleTerminalProbe()
			},
			goToWizardStep(index){
				this.wizardStepIndex = Math.min(Math.max(index, 0), this.accessWizardSteps.length - 1)
			},
			previousWizardStep(){
				this.goToWizardStep(this.wizardStepIndex - 1)
			},
			nextWizardStep(){
				if (!this.validateCurrentWizardStep()) return
				this.goToWizardStep(this.wizardStepIndex + 1)
			},
			validateCurrentWizardStep(){
				const step = this.currentWizardStep ? this.currentWizardStep.key : ''
				if (step === 'vendor' && !this.form.vendor) return this.notifyStepWarning('access_control_vendor_required')
				if (step === 'model' && !this.form.model) return this.notifyStepWarning('access_control_model_required')
				if (step === 'terminal' && (!this.form.name || !this.form.host || !this.form.username || (!this.isEdit && !this.form.password))) return this.notifyStepWarning('access_control_terminal_required')
				if (step === 'video' && !this.isRtspValid) return this.notifyStepWarning('access_control_rtsp_invalid')
				return true
			},
			notifyStepWarning(key){
				this.notify('warning', this.$ml.get(key))
				return false
			},
			scheduleTerminalProbe(){
				if (this.probeTimer) clearTimeout(this.probeTimer)
				if (!this.canProbeTerminal) return
				this.probeTimer = setTimeout(() => this.probeTerminalInfo(true), 600)
			},
			async probeTerminalInfo(silent = false){
				if (!this.canProbeTerminal || this.probeLoading) return false
				this.probeLoading = true
				if (!silent) {
					this.probeMessage = this.$ml.get('access_control_probe_running')
					this.probeMessageType = 'info'
				}
				try {
					const res = await AccessControlService.probeDevice({
						vendor: this.form.vendor,
						model: this.form.model,
						host: this.form.host,
						username: this.form.username,
						password: this.form.password,
					})
					this.applyProbeInfo(res && res.data ? res.data : res)
					this.probeMessage = this.$ml.get('access_control_probe_success')
					this.probeMessageType = 'success'
					return true
				} catch (e) {
					this.probeMessage = this.$ml.get('access_control_probe_unavailable')
					this.probeMessageType = 'warning'
					return false
				} finally {
					this.probeLoading = false
				}
			},
			applyProbeInfo(payload){
				const data = payload && payload.data && typeof payload.data === 'object' ? payload.data : payload
				if (!data || typeof data !== 'object') return
				if (data.vendor) this.form.vendor = normalizeAccessControlVendor(data.vendor) || this.form.vendor
				if (data.model) this.form.model = data.model
				if (data.serial || data.deviceSn || data.device_sn) this.form.serial = data.serial || data.deviceSn || data.device_sn
				if (!this.form.name && (data.name || data.deviceName)) this.form.name = data.name || data.deviceName
				if (!this.form.rtsp && (data.rtsp || data.rtspUrl || data.streamUrl)) this.form.rtsp = data.rtsp || data.rtspUrl || data.streamUrl
			},
			async loadServerLocation(){
				this.serverLocationLoading = true
				try {
					const res = await AccessControlService.getServerLocation()
					const data = res && res.data ? res.data : res
					const latitude = data && (data.latitude !== undefined ? data.latitude : data.lat)
					const longitude = data && (data.longitude !== undefined ? data.longitude : data.lng)
					if (this.hasCoordinate(latitude) && this.hasCoordinate(longitude) && !this.locationTouched) {
						this.form.latitude = latitude
						this.form.longitude = longitude
						this.serverLocationMessage = this.$ml.get('access_control_server_location_loaded')
						this.serverLocationMessageType = 'success'
					} else {
						throw new Error('No server coordinates')
					}
				} catch (e) {
					this.serverLocationMessage = this.$ml.get('access_control_server_location_unavailable')
					this.serverLocationMessageType = 'warning'
				} finally {
					this.serverLocationLoading = false
				}
			},
			shouldHydrateFromLocalSettings(){
				return !(this.$route && this.$route.name === 'new_access_control')
			},
			loadFromLocalSettings(){
				try {
					const raw = localStorage.getItem('settings_device')
					if (!raw) return

					const parsed = JSON.parse(raw)
					const item = parsed && parsed.inf_setting ? parsed.inf_setting : null
					if (item && item.type === 'ACCESS_CONTROL') {
						this.renderInfo(item)
						this.refreshDeviceDetail(item)
					}
				} catch (e) {}
			},
			async refreshDeviceDetail(item){
				const deviceId = item && (item.deviceId || item.device_id || item.id)
				if (!deviceId) return

				try {
					const res = await AccessControlService.getDevice(deviceId)
					if (!res || !res.data) return

					const detail = { ...item, ...res.data, type: 'ACCESS_CONTROL' }
					this.renderInfo(detail)
					localStorage.setItem('settings_device', JSON.stringify({ inf_setting: sanitizeAccessControlDevice(detail) }))
				} catch (e) {}
			},
			renderInfo(item){
				const rawRtsp = item && (item.rtsp || item.rtspUrl || item.streamUrl) ? (item.rtsp || item.rtspUrl || item.streamUrl) : ''
				const sanitized = sanitizeAccessControlDevice(item) || {}
				this.form = {
					...emptyForm(),
					...sanitized,
					deviceId: sanitized.deviceId || sanitized.device_id || sanitized.id || '',
					vendor: normalizeAccessControlVendor(sanitized.vendor) || 'dahua',
					host: sanitized.host || sanitized.ip || '',
					brand: sanitized.brand || '',
					model: sanitized.model || '',
					serial: sanitized.serial || sanitized.deviceSn || sanitized.device_sn || '',
					longitude: sanitized.longitude !== undefined && sanitized.longitude !== null ? sanitized.longitude : '',
					latitude: sanitized.latitude !== undefined && sanitized.latitude !== null ? sanitized.latitude : '',
					username: sanitized.username || sanitized.user || '',
					password: '',
					rtsp: rawRtsp,
					type: 'ACCESS_CONTROL',
				}
				this.hasPassword = !!(sanitized.hasPassword || sanitized.has_password || sanitized.credentialRedacted)
				this.showPreview = false
				this.previewError = ''
				this.locationTouched = false
				this.saveAttempted = false
				this.wizardStepIndex = 0
				this.probeMessage = ''
				this.loadVendorModels(this.form.vendor)
			},
			hasCoordinate(value){
				return value !== undefined && value !== null && value !== '' && !Number.isNaN(Number(value))
			},
			buildHlsUrl(deviceId, channel){
				if (!deviceId) return '-'
				return `/hls/${deviceId}/${channel}/index.m3u8`
			},
			setLocation(location = {}){
				this.form.longitude = location.longitude
				this.form.latitude = location.latitude
				this.locationTouched = true
			},
			notify(type, message){
				const bus = this.$EventBus || EventBus
				bus.$emit('notifications_modal', { type, message })
			},
			testStream(){
				if (!this.form.rtsp) {
					this.showPreview = false
					this.previewError = ''
					this.notify('warning', this.$ml.get('access_control_rtsp_required'))
					return
				}
				if (!this.isRtspValid) {
					this.showPreview = false
					this.previewError = ''
					this.notify('warning', this.$ml.get('access_control_rtsp_invalid'))
					return
				}
				this.previewError = ''
				this.showPreview = !this.showPreview
			},
			handlePreviewFailure(){
				this.previewError = this.previewFailureMessage
				this.showPreview = true
				this.notify('warning', this.previewFailureMessage)
			},
			async togglePasswordVisibility(){
				if (this.isEdit && this.hasPassword && !this.form.password) {
					if (!this.canEditDevice) return
					const revealed = await this.revealSavedPassword()
					if (!revealed) return
				}

				if (this.showPassword) {
					this.showPassword = false
					return
				}
				this.showPassword = true
			},
			async revealSavedPassword(){
				const deviceId = this.form.deviceId || this.form.id
				if (!deviceId) return false

				this.passwordRevealLoading = true
				try {
					const res = await AccessControlService.getDevicePassword(deviceId)
					const password = res && res.data ? res.data.password : ''
					if (!password) {
						this.notify('warning', 'Saved password is unavailable')
						return false
					}
					this.form.password = password
					this.hasPassword = true
					return true
				} catch (e) {
					this.notify('warning', 'Saved password is unavailable')
					return false
				} finally {
					this.passwordRevealLoading = false
				}
			},
			isBackendDeviceIdContractError(error){
				const data = error && error.response ? error.response.data : null
				const message = typeof data === 'string' ? data : JSON.stringify(data || '')
				return /deviceId.*required|required.*deviceId/i.test(message)
			},
			isRtspPersistenceError(error){
				if (!this.form.rtsp) return false
				const status = error && error.response ? error.response.status : null
				if (status === 501) return true

				const data = error && error.response ? error.response.data : null
				const message = typeof data === 'string' ? data : JSON.stringify(data || '')
				return /rtsp|stream|unsupported|unknown field|persist|dependency/i.test(message)
			},
			buildPayload(){
				const payload = {
					host: this.form.host,
					vendor: normalizeAccessControlVendor(this.form.vendor) || 'dahua',
					model: this.form.model,
					serial: this.form.serial,
					username: this.form.username,
					password: this.form.password,
					name: this.form.name,
					longitude: this.form.longitude,
					latitude: this.form.latitude,
					rtsp: this.form.rtsp ? this.form.rtsp.trim() : '',
				}
				if (this.isEdit && !payload.password) delete payload.password
				if (!this.isEdit && !payload.rtsp) delete payload.rtsp
				return payload
			},
			validateWizardBeforeSave(){
				if (!this.form.vendor) return this.notifyStepWarning('access_control_vendor_required')
				if (!this.form.model) return this.notifyStepWarning('access_control_model_required')
				if (!this.form.name || !this.form.host || !this.form.username || (!this.isEdit && !this.form.password)) return this.notifyStepWarning('access_control_terminal_required')
				return true
			},
			async save(){
				this.saveAttempted = true
				if (!this.validateWizardBeforeSave()) return
				if (this.$refs.form && !this.$refs.form.validate()) return
				if (!this.isRtspValid) {
					this.notify('warning', this.$ml.get('access_control_rtsp_invalid'))
					return
				}
				if (!this.hasLocation) {
					this.notify('warning', `${this.$ml.get('location') || 'Location'} ${this.$ml.get('required') || 'required'}`)
					return
				}
				const permission = this.isEdit ? 'can_edit' : 'can_add'
				if (!this.$store.getters['auth/permission'](this.$route.name || 'new_access_control', permission, 'device')) {
					this.notify('warning', this.$ml.get('no_have_permission_create') || 'Permission denied')
					return
				}

				this.loading = true
				try {
					const payload = this.buildPayload()
					const key = this.form.deviceId || this.form.id
					const hadPassword = this.hasPassword
					const passwordWasProvided = !!payload.password
					const res = this.isEdit ? await AccessControlService.updateDevice(key, payload) : await AccessControlService.createDevice(payload)
					const responseData = res && res.data ? res.data : {}
					const saved = sanitizeAccessControlDevice({
						...this.form,
						...payload,
						...responseData,
						deviceId: responseData.deviceId || responseData.device_id || responseData.id || this.form.deviceId || this.form.id,
						host: responseData.host || responseData.ip || payload.host || this.form.host,
						username: responseData.username || responseData.user || payload.username || this.form.username,
						type: 'ACCESS_CONTROL',
					})
					this.form.password = ''
					this.hasPassword = !!(saved && (saved.hasPassword || saved.has_password || saved.credentialRedacted || passwordWasProvided || hadPassword))
					if (this.hasPassword) saved.hasPassword = true

					if (this.isEdit) {
						this.$store.commit('Devices/update', saved)
						EventBus.$emit('update_device_to_comp', saved)
					} else {
						this.$store.commit('Devices/concat_devices', saved)
					}

					localStorage.setItem('settings_device', JSON.stringify({ inf_setting: saved }))
					this.notify(this.isEdit ? 'updated' : 'saved')

					if (!this.isEdit) {
						EventBus.$emit('redirect_view', 'setting_device')
						this.$router.push({ name: 'setting_device' }).catch(err => {
							if (err.name !== 'NavigationDuplicated') throw err
						})
					}
				} catch (e) {
					if (this.isBackendDeviceIdContractError(e)) {
						this.notify('warning', this.backendDeviceIdContractMessage)
					} else if (this.isRtspPersistenceError(e)) {
						this.notify('warning', this.backendRtspDependencyMessage)
					} else {
						this.notify('error')
					}
				} finally {
					this.loading = false
				}
			},
		},
		beforeDestroy(){
			if (this.probeTimer) clearTimeout(this.probeTimer)
		},
	}
</script>

<style scoped>
	.access-control-location-selector{
		min-height: 420px;
	}

	.access-control-device-page{
		padding-top: 24px !important;
		max-width: 1280px;
		margin-left: auto;
		margin-right: auto;
	}

	.access-control-device-page--embedded{
		padding-top: 0 !important;
		max-width: none;
		width: 100%;
	}

	.access-control-device-card{
		margin-top: 8px;
		width: 100%;
	}

	.access-control-settings-region{
		min-width: 0;
	}

	.access-control-settings-form{
		width: 100%;
	}

	.access-control-preview-column{
		min-height: 260px;
	}

	.access-control-preview-card{
		overflow: hidden;
	}

	.access-control-preview-frame{
		aspect-ratio: 16 / 9;
		min-height: 220px;
	}
</style>
